// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for managing unified inventory data with view selection and filtering.
 * This module provides functionality for fetching different types of inventory
 * (all, bulk items, assets, assemblies) with flexible filtering options.
 */
import { useLocalStorage } from '@/hooks/use-local-storage';
import { getAllInventory } from '@/inventory/api/get-all-inventory';
import { getAssembliesInventory } from '@/inventory/api/get-assemblies-inventory';
import { getAssetsInventory } from '@/inventory/api/get-assets-inventory';
import { getBulkItemsInventory } from '@/inventory/api/get-bulk-items-inventory';
import {
  InventoryByLocationResponse,
  getInventoryByLocation,
} from '@/inventory/api/get-inventory-by-location';
import {
  AllInventoryResponse,
  AssembliesInventoryResponse,
  AssetsInventoryResponse,
  BulkItemsInventoryResponse,
  InventoryFilters,
} from '@/inventory/api/types';
import {
  normalizeFilters,
  serializeFiltersToKey,
} from '@/inventory/utilities/serialize-filters';
import { THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Available inventory types (what you're viewing)
 */
type InventoryType = 'all' | 'bulk-items' | 'assets' | 'assemblies';

/**
 * Available inventory dimensions (how you're grouping)
 */
export type InventoryDimension = 'item' | 'location';

/**
 * Schema for validating inventory dimension selection
 */
const inventoryDimensionSchema = z.enum(['item', 'location']);

/**
 * Default inventory dimension
 */
const DEFAULT_INVENTORY_DIMENSION: InventoryDimension = 'item';

/**
 * Hook for managing inventory dimension selection with local storage persistence
 */
export function useInventoryDimensionFilter() {
  const [dimension, setDimension] = useLocalStorage(
    'inventory-dimension-filter',
    inventoryDimensionSchema
  );

  const currentDimension = dimension ?? DEFAULT_INVENTORY_DIMENSION;

  const handleChangeDimension = (newDimension: InventoryDimension) => {
    setDimension(newDimension);
  };

  return {
    dimension: currentDimension,
    onChangeDimension: handleChangeDimension,
  };
}

/**
 * Configuration options for the useInventory hook
 */
interface UseInventoryOptions {
  type: InventoryType;
  dimension: InventoryDimension;
  filters?: InventoryFilters;
  enabled?: boolean;
}

/**
 * Base result interface with common properties
 */
interface BaseInventoryResult {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Result interface for all inventory view
 */
interface AllInventoryResult extends BaseInventoryResult {
  view: 'all';
  inventory?: AllInventoryResponse;
  refetch: () => void;
}

/**
 * Result interface for bulk items inventory view
 */
interface BulkItemsInventoryResult extends BaseInventoryResult {
  view: 'bulk-items';
  inventory?: BulkItemsInventoryResponse;
  refetch: () => void;
}

/**
 * Result interface for assets inventory view
 */
interface AssetsInventoryResult extends BaseInventoryResult {
  view: 'assets';
  inventory?: AssetsInventoryResponse;
  refetch: () => void;
}

/**
 * Result interface for assemblies inventory view
 */
interface AssembliesInventoryResult extends BaseInventoryResult {
  view: 'assemblies';
  inventory?: AssembliesInventoryResponse;
  refetch: () => void;
}

/**
 * Result interface for location-grouped inventory view
 */
interface LocationInventoryResult extends BaseInventoryResult {
  view: 'location';
  inventory?: InventoryByLocationResponse;
  refetch: () => void;
}

/**
 * Union type for all possible inventory results
 */
type InventoryResult =
  | AllInventoryResult
  | BulkItemsInventoryResult
  | AssetsInventoryResult
  | AssembliesInventoryResult
  | LocationInventoryResult;

/**
 * A React hook that fetches and manages inventory data with view toggling and filtering.
 * Supports fetching different types of inventory data based on the selected view:
 * - 'all': Bulk items, assets, and assemblies in one response
 * - 'bulk-items': Only bulk items inventory data
 * - 'assets': Only assets NOT in inventory
 * - 'assemblies': Only assemblies/products inventory data
 *
 * The hook supports:
 * - View-based data fetching with automatic query switching
 * - Optional filtering by item key, location ID, product key, and customer ID
 * - Automatic background updates and caching via React Query
 * - Loading and error state management
 * - Manual refetch capability
 *
 * @param options - Configuration options for view selection, filtering, and query behavior
 * @returns Object containing the inventory data and query state based on the selected view
 *
 * @example
 * ```tsx
 * // Fetch all inventory types
 * const { inventory, isLoading, view } = useInventory({ view: 'all' });
 *
 * // Fetch only bulk items inventory with location filter
 * const { inventory, isLoading } = useInventory({
 *   type: 'bulk-items',
 *   filters: { locationIds: ['loc-123'] }
 * });
 *
 * // Conditionally fetch based on some condition
 * const { inventory, isLoading } = useInventory({
 *   view: 'assets',
 *   enabled: shouldFetch
 * });
 * ```
 */
export function useInventory(options: UseInventoryOptions): InventoryResult {
  const { type, dimension, filters, enabled = true } = options;

  // Normalize filters and create a stable query key
  const normalizedFilters = normalizeFilters(filters);
  const filtersKey = serializeFiltersToKey(normalizedFilters);

  // Define common query options
  const baseQueryOptions = {
    enabled,
    staleTime: 0, // Always stale - refetch immediately on mount/access
    refetchInterval: THIRTY_SECONDS_MS,
    refetchOnWindowFocus: true,
  };

  // Query for 'all' type - gets unified inventory
  const allInventoryResult = useQuery({
    queryKey: ['inventory', 'all', filtersKey],
    queryFn: () => getAllInventory(normalizedFilters),
    ...baseQueryOptions,
    enabled: enabled && dimension === 'item' && type === 'all',
  });

  // Query for 'bulk-items' type - gets only bulk items
  const bulkItemsInventoryResult = useQuery({
    queryKey: ['inventory', 'bulk-items', filtersKey],
    queryFn: () => getBulkItemsInventory(normalizedFilters),
    ...baseQueryOptions,
    enabled: enabled && dimension === 'item' && type === 'bulk-items',
  });

  // Query for 'assets' type - gets only assets
  const assetsInventoryResult = useQuery({
    queryKey: ['inventory', 'assets', filtersKey],
    queryFn: () => getAssetsInventory(normalizedFilters),
    ...baseQueryOptions,
    enabled: enabled && dimension === 'item' && type === 'assets',
  });

  // Query for 'assemblies' type - gets only assemblies
  const assembliesInventoryResult = useQuery({
    queryKey: ['inventory', 'assemblies', filtersKey],
    queryFn: () => getAssembliesInventory(normalizedFilters),
    ...baseQueryOptions,
    enabled: enabled && dimension === 'item' && type === 'assemblies',
  });

  // Location-grouped inventory query
  const locationInventoryResult = useQuery({
    queryKey: ['inventory', 'location', type, filtersKey],
    queryFn: () => getInventoryByLocation(type, normalizedFilters),
    ...baseQueryOptions,
    enabled: enabled && dimension === 'location',
  });

  // Return the appropriate result based on dimension and type
  if (dimension === 'location') {
    return {
      view: 'location',
      inventory: locationInventoryResult.data,
      isLoading: locationInventoryResult.isLoading,
      isFetching: locationInventoryResult.isFetching,
      isError: locationInventoryResult.isError,
      error: locationInventoryResult.error,
      refetch: () => {
        void locationInventoryResult.refetch();
      },
    } as LocationInventoryResult;
  }

  // Handle item dimension with different types using a map for DRY code
  const queryResultMap = {
    all: allInventoryResult,
    'bulk-items': bulkItemsInventoryResult,
    assets: assetsInventoryResult,
    assemblies: assembliesInventoryResult,
  } as const;

  const result = queryResultMap[type];

  return {
    view: type,
    inventory: result.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: () => {
      void result.refetch();
    },
  } as InventoryResult;
}
